import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { Brief, Field as FieldDef, Issue, Link as Linked, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { linkId, linkValues, makeLink } from "../../lib/links.ts";
import { canEdit } from "../../lib/roles.ts";
import { issueKey } from "../../lib/state.ts";
import { Button } from "../../ui/Button.tsx";
import { Combobox } from "../../ui/Combobox.tsx";
import { CloseIcon } from "../../ui/icons.tsx";
import { LINK, TH } from "../../ui/styles.ts";
import { searchItems } from "../items/search.tsx";
import { LinkedTarget } from "./linkedItems.tsx";
import { ValueInput } from "./valueInputs.tsx";

interface Props {
  space: SpaceOut;
  /** A ref field with link fields. */
  field: FieldDef;
  links: readonly Linked[];
  /** The linked items, resolved. */
  briefs: readonly Brief[];
  /** The item's issues; those about a link show under its row. */
  issues: readonly Issue[];
  renderIssue: (i: Issue) => ComponentChildren;
  onChange: (links: Linked[]) => void;
}

/**
 * A ref field whose links carry values (bottles and unit cost per wine): one row per linked item with an input per
 * link field, a remove button, and a search to add items. The issues about a link show under its row.
 */
export function LinkTable(p: Props) {
  const fields = p.field.fields ?? [];
  const [titles, setTitles] = useState<Record<string, string>>({});
  // Read-only: its links, without ways to change them.
  const editable = canEdit(p.space);
  const brief = (id: string) => p.briefs.find((b) => b.id === id);
  const title = (id: string) =>
    brief(id)?.hidden ? t("item.privateItem") : titles[id] || brief(id)?.title || t("common.untitled");
  const setValue = (i: number, key: string, v: unknown) =>
    p.onChange(p.links.map((l, j) => (j === i ? makeLink(linkId(l), { ...linkValues(l), [key]: v }) : l)));
  const prefix = (i: number) => `refs.${p.field.key}[${i}]`;
  const rowIssues = (i: number) => p.issues.filter((x) => x.path === prefix(i) || x.path?.startsWith(`${prefix(i)}.`));
  return (
    <div class="flex flex-col gap-2">
      {p.links.length > 0 ? (
        <div class="relative overflow-x-auto rounded-md border border-zinc-200 bg-white">
          <table class="min-w-full text-sm">
            <thead class="bg-zinc-50">
              <tr>
                <th scope="col" class={TH}>
                  {p.field.label || p.field.key}
                </th>
                {fields.map((f) => (
                  <th key={f.key} scope="col" class={TH}>
                    {f.label || f.key}
                    {f.required ? <span class="text-red-600"> *</span> : null}
                  </th>
                ))}
                <th scope="col">
                  <span class="sr-only">{t("common.remove")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {p.links.map((l, i) => {
                const id = linkId(l);
                // The same item twice (flagged as a duplicate) still needs distinct keys.
                const nth = p.links.slice(0, i).filter((x) => linkId(x) === id).length;
                const values = linkValues(l);
                const issues = rowIssues(i);
                return [
                  <tr key={`${id}#${nth}`} class="border-t border-zinc-100">
                    <td class="px-3 py-2 align-top whitespace-nowrap">
                      <LinkedTarget space={p.space} id={id} brief={brief(id)} title={title(id)} linkClass={LINK} />
                    </td>
                    {fields.map((f) => (
                      <td key={f.key} class="min-w-32 px-2 py-1.5 align-top">
                        <ValueInput
                          field={{
                            ...f,
                            time_zone: f.time_zone ?? p.space.time_zone,
                            region: f.region ?? p.space.region ?? null,
                          }}
                          label={t("fields.linkValue", { field: f.label || f.key, item: title(id) })}
                          value={values[f.key]}
                          onChange={(v) => setValue(i, f.key, v)}
                        />
                      </td>
                    ))}
                    <td class="px-2 py-1.5 text-right align-top">
                      {editable ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t("fields.unlink", { title: title(id) })}
                          onClick={() => p.onChange(p.links.filter((_, j) => j !== i))}
                        >
                          <CloseIcon size={14} />
                        </Button>
                      ) : null}
                    </td>
                  </tr>,
                  issues.length > 0 ? (
                    <tr key={`${id}#${nth}:issues`}>
                      <td colSpan={fields.length + 2} class="px-3 pb-2 text-xs text-red-700">
                        {issues.map((x) => (
                          <span key={issueKey(x)} class="block">
                            {p.renderIssue(x)}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {editable ? (
        <Combobox
          label={t("fields.addLink", { field: p.field.label || p.field.key })}
          placeholder={t("common.searchItems")}
          search={(q, signal) => searchItems(p.space, q, signal, { entity: p.field.target ?? null })}
          onSelect={(o) => {
            setTitles({ ...titles, [o.value]: o.label });
            p.onChange(p.field.many ? [...p.links, o.value] : [o.value]);
          }}
        />
      ) : null}
    </div>
  );
}
